import Veraportal from "./Veraportal";
import Modal from "./Modal";
import Schuelerverwaltung from "./Schuelerverwaltung";
import Utilities from "./Utilities";
import {IJsontresorDB} from "./InterfacesDB";
import VeraApi from "./VeraApi";
import type {PDFDocument, PDFForm} from 'pdf-lib';
import Statistik from "./Statistik";
import Tooltip from './Tooltip';

declare const PDFLib: any;

/**
 * Singleton-Klasse zur Verwaltung der clientseitig ver- und entschlüsselten Schüler-Klarnamen.
 * Der "Tresor" speichert die Namen nur im Browser des Nutzers und auf dem Server in verschlüsselter Form.
 * Die Entschlüsselung findet ausschließlich clientseitig statt.
 */
export default class Tresor {
    private _veraportal: Veraportal;

    private _eventOnTresorOffen: Event; // Event, das bei erfolgreicher Entschlüsselung ausgelöst wird.
    public offen: boolean = false; // Status, ob der Tresor entschlüsselt und nutzbar ist.

    public changed: boolean = false; // Status, ob ungespeicherte Änderungen vorliegen.
    private _encrypted: string = null; // AES-verschlüsselter und GZIP-komprimierter JSON-String der Klarnamen.
    private _decrypted: { schuelers: { nr: number; klarname: string }[] } = null; // Entschlüsseltes Objekt mit Klarnamen.
    private _passphrase: string = null; // Der 12-stellige Code zur Entschlüsselung.
    private _salt: string = null; // Zufälliger Salt zur Schlüsselableitung.

    private static _instance: Tresor;

    /**
     * Gibt die Singleton-Instanz der Tresor-Klasse zurück.
     * @param {Veraportal} veraportal - Die Hauptinstanz der Veraportal-Anwendung.
     * @returns {Tresor}
     */
    public static getInstance(veraportal: Veraportal): Tresor {
        return this._instance || (this._instance = new this(veraportal));
    }

    private constructor(veraportal: Veraportal) {
        this._veraportal = veraportal;

        this._eventOnTresorOffen = new CustomEvent("onTresorOffen", {"detail": "Tresor wurde geöffnet"});
        this.asyncConstructor();
    }

    /**
     * Führt die asynchrone Initialisierung durch.
     * Lädt Tresordaten aus dem localStorage und versucht, diese zu entschlüsseln.
     * Setzt den Status des Tresors im DOM und in der Instanz.
     * @private
     */
    private async asyncConstructor(): Promise<void> {
        let tresorSchalterDOM = $(`#tresor-schalter[data-tresor-offen]`);

        // Tresorvariablen aus dem localStorage zur Wiederherstellung der Sitzung einlesen
        this._passphrase = JSON.parse(localStorage.getItem('tresor-passphrase'));
        this._salt = JSON.parse(localStorage.getItem('tresor-salt'));
        this._encrypted = JSON.parse(localStorage.getItem('tresor-encrypted'));

        // Versuch, die Klarnamen nach dem Seitenaufruf automatisch zu entschlüsseln
        if (this._passphrase !== null || this._salt !== null || this._encrypted !== null) {
            try {
                let kdf = CryptoJS.PBKDF2(this._passphrase, this._salt, {keySize: 512 / 32, iterations: 1000});
                let jsonString = await Utilities.decompressStringWithGzip(
                    CryptoJS.enc.Utf8.stringify(CryptoJS.AES.decrypt(this._encrypted, CryptoJS.enc.Base64.stringify(kdf)))
                );
                this._decrypted = JSON.parse(jsonString);
            } catch (e) {
                // Bei einem Fehler werden die ungültigen Tresordaten aus dem localStorage entfernt.
                let hinweisModal = new Modal(this._veraportal);
                hinweisModal.show({
                    title: `Entschlüsselung nicht gelungen`,
                    body: `<p>Bei der Entschlüsselung der Klarnamen ist etwas schiefgelaufen. Bitte entschlüsseln Sie Ihre Klarnamen erneut.
                            <br/>Fehlercode: 3633677</p>`,
                    hasSubmit: false,
                    hasCancel: true,
                    hasBestaetigung: false,
                    cancelText: 'OK',
                    width: 400,
                });
                localStorage.removeItem('tresor-passphrase');
                localStorage.removeItem('tresor-salt');
                localStorage.removeItem('tresor-encrypted');

                Utilities.errorToSentry(e);
            }
        }

        // Zustand des Tresors im DOM widerspiegeln und bei Inkonsistenzen zurücksetzen
        if (this._passphrase === null || this._salt === null || this._encrypted === null || this._decrypted === null) {
            this.offen = false;
            tresorSchalterDOM.attr('data-tresor-offen', '0');
            localStorage.removeItem('tresor-passphrase');
            localStorage.removeItem('tresor-salt');
            localStorage.removeItem('tresor-encrypted');
        } else {
            this.offen = true;
            tresorSchalterDOM.attr('data-tresor-offen', '1');
            document.dispatchEvent(this._eventOnTresorOffen); // Trigger onTresorOffen
        }
    }

    /**
     * Ruft die verschlüsselten Tresordaten (Salt und AES-Blob) vom Server ab.
     * @returns {Promise<IJsontresorDB | false>}
     */
    public async fetchTresor(): Promise<IJsontresorDB | false> {
        try {
            const response = await fetch(`/api/tresor.php/schulnummer/${this._veraportal.sessionvariablen.schule.schulnummer}`, {
                method: 'GET',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000)
            });
            if (response.ok === false) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
        }
    }

    /**
     * Sendet die aktuell verschlüsselten Tresordaten (Salt und AES-Blob) an den Server, um sie zu speichern.
     * @returns {Promise<boolean>}
     */
    public async postTresor(): Promise<boolean> {
        try {
            const response = await fetch(`/api/tresor.php/`, {
                method: 'POST',
                headers: new Headers({
                    'content-type': 'application/json',
                    'cache-control': 'no-store'
                }),
                signal: AbortSignal.timeout(20000),
                body: JSON.stringify({
                    schulnummer: this._veraportal.sessionvariablen.schule.schulnummer,
                    jsontresor_array: [{
                        json_aes_blob: this._encrypted,
                        salt: this._salt
                    }]
                })
            });
            if (response.ok === false) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (e) {
            Utilities.errorToSentry(e);

            if (e.name === 'TimeoutError') {
                console.log('timeout')
            }
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Verschlüsselte Klarnamen konnten nicht gespeichert werden`,
                body: `<p>
                Beim Speichern der Klarnamen ist ein Fehler aufgetreten.<br/>
                Bitte wenden Sie sich an unseren ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Support mit dem Fehlercode 16753, falls das Problem weiterhin besteht.
                </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 200,
                cancelText: 'OK'
            });

            Utilities.errorToSentry(e);
        }
    }

    /**
     * Aktiviert die Klarnamenfunktion. Prüft, ob bereits ein Tresor auf dem Server existiert.
     * Wenn ja, wird die erneute Öffnung gestartet (`erneutOeffnen`).
     * Wenn nein, wird ein neuer Tresor initialisiert (`initialisieren`).
     */
    public async aktivieren() {
        let tresor = await this.fetchTresor();
        if (tresor === false) {
            await this.initialisieren();
        } else {
            this.erneutOeffnen(tresor);
        }
    }

    /**
     * Öffnet einen bereits existierenden Tresor, indem der Nutzer zur Eingabe des Entschlüsselungscodes aufgefordert wird.
     * @param {IJsontresorDB} result - Die vom Server abgerufenen Tresordaten (Salt, AES-Blob).
     * @returns {boolean}
     * @private
     */
    private erneutOeffnen(result: IJsontresorDB): boolean {
        if (result?.salt === undefined || result?.json_aes_blob === undefined)
            return false;

        // Modal zur Abfrage des Entschlüsselungscodes anzeigen
        let tresorOeffnenModal = new Modal(this._veraportal);
        let tresorOeffnenModalPromise = tresorOeffnenModal.show({
            id: `modal-tresor-oeffnen`,
            title: `Klarnamen entschlüsseln`,
            body: `<p>Geben Sie den zwölfstelligen Entschlüsselungscode ein, der bei der Aktivierung
                                der Klarnamenfunktion vergeben wurde.</p>
                                <label>
                                    <b>Entschlüsselungscode:</b><br/>
                                    <input type="text" id="modal-tresor-oeffnen-code-input1" size="5" maxlength="4"/>
                                    <input type="text" id="modal-tresor-oeffnen-code-input2" size="5" maxlength="4"/>
                                    <input type="text" id="modal-tresor-oeffnen-code-input3" size="5" maxlength="4"/>
                                </label>
                    <p>
                    <br/><br/>
                        Sollten Sie den Entschlüsselungscode nicht mehr mit Ihrem Kollegium rekonstruieren können,
                        können Sie mit folgendem Button alle Klarnamen vollständig zurücksetzen und dann neu im Portal hinterlegen:
                    </p>
                    <button id="klarnamen-zuruecksetzen-btn" tabindex="-1">
                        <svg role="graphics-symbol">
                            <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#trash"/>
                        </svg>
                        Klarnamen zurücksetzen
                    </button>
                    `,
            initFunction: () => {
                // Eingabe in den Code-Feldern auf alphanumerische Zeichen beschränken
                document.querySelectorAll(`#modal-tresor-oeffnen-code-input1, #modal-tresor-oeffnen-code-input2, #modal-tresor-oeffnen-code-input3`)
                    ?.forEach(i =>
                        i.addEventListener('keyup', (evt) => {
                            Utilities.forceKeys((evt.target as HTMLInputElement), 'A-Za-z0-9', true, false);
                        })
                    );

                // Eingefügten Text aus der Zwischenablage auf die drei Code-Felder verteilen
                document.getElementById('modal-tresor-oeffnen-code-input1')
                    ?.addEventListener('paste', (e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        // @ts-ignore
                        let clipboardData = e.clipboardData || window.clipboardData;
                        let pastedData = clipboardData?.getData('Text').trim();
                        pastedData = pastedData?.split(' ').join('');

                        if (pastedData.length <= 12) {
                            (document.querySelector(`#modal-tresor-oeffnen-code-input1`) as HTMLInputElement).value = pastedData.substring(0, 4);
                            (document.querySelector(`#modal-tresor-oeffnen-code-input2`) as HTMLInputElement).value = pastedData.substring(4, 8);
                            (document.querySelector(`#modal-tresor-oeffnen-code-input3`) as HTMLInputElement).value = pastedData.substring(8, 12);
                        }
                    });

                (document.querySelector(`#modal-tresor-oeffnen-code-input1`) as HTMLInputElement)
                    ?.dispatchEvent(new Event('focus'));

                // Event-Handler für den "Klarnamen zurücksetzen"-Button
                document.querySelector(`#klarnamen-zuruecksetzen-btn`)
                    ?.addEventListener('click', () => {
                        let zuruecksetzenModal = new Modal(this._veraportal);
                        let zuruecksetzenModalPromise = zuruecksetzenModal.show({
                            title: `Klarnamen Ihres ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Jahrgangs zurücksetzen`,
                            body: `<p>Möchten Sie die Klarnamen Ihrers ${this._veraportal.sessionvariablen.projekt.bezeichnung}-Jahrganges wirklich vollständig zurücksetzen? Sonstige Schüler-Eigenschaften bleiben davon unberührt!<br/>
                                Sie erhalten, wenn sie erneut auf "Klarnamen aktivieren" klicken, einen neuen Entschlüsselungscode und können die Namen im Anschluss wieder eingeben.</p>
                                <p>Wenn Sie damit einverstanden sind, dass alle bisher hinterlegten Klarnamen vollständig zurückgesetzt werden, geben Sie im Feld unten <i>"OK"</i> ein.</p>`,
                            hasSubmit: true,
                            hasCancel: true,
                            hasBestaetigung: true,
                            width: 700,
                            cancelText: 'Abbrechen',
                            submitText: 'Klarnamen zurücksetzen'
                        });
                        zuruecksetzenModalPromise.then(async (modalantwort) => {
                            if (modalantwort === true) {
                                try {
                                    const api = VeraApi.getInstance(this._veraportal);
                                    let deleteGruppeResponse = await api.deleteTresor([{}]);
                                    if (deleteGruppeResponse === true) {
                                        setTimeout(() => {
                                            window.location.reload();
                                        }, 1000);
                                    }
                                } catch {
                                    //
                                }
                            }
                        });
                    });

            },
            hasSubmit: true,
            hasCancel: true,
            hasBestaetigung: false,
            submitText: 'Klarnamen entschlüsseln',
            width: 700,
        });

        tresorOeffnenModalPromise.then(async (modalantwort) => {
            if (modalantwort === true) {
                // Entschlüsselungscode zusammensetzen und zusammen mit Server-Daten im localStorage speichern
                this._passphrase = $(`#modal-tresor-oeffnen-code-input1`)?.val().toString() + $(`#modal-tresor-oeffnen-code-input2`)?.val().toString() + $(`#modal-tresor-oeffnen-code-input3`)?.val().toString();
                localStorage.setItem("tresor-passphrase", JSON.stringify(this._passphrase));
                this._salt = result?.salt;
                localStorage.setItem("tresor-salt", JSON.stringify(this._salt));
                this._encrypted = result?.json_aes_blob;
                localStorage.setItem("tresor-encrypted", JSON.stringify(this._encrypted));

                // Versuch, den AES-Blob mit dem eingegebenen Code zu entschlüsseln
                try {
                    let kdf = CryptoJS.PBKDF2(this._passphrase, this._salt, {keySize: 512 / 32, iterations: 1000});
                    let jsonString = await Utilities.decompressStringWithGzip(
                        CryptoJS.enc.Utf8.stringify(
                            CryptoJS.AES.decrypt(
                                this._encrypted, CryptoJS.enc.Base64.stringify(kdf)
                            )
                        )
                    );
                    this._decrypted = JSON.parse(jsonString);
                    this.offen = true;
                    $(`#tresor-schalter[data-tresor-offen]`).attr('data-tresor-offen', '1');
                    document.dispatchEvent(this._eventOnTresorOffen);

                    this.fillTresorLuecken();
                    try {
                        // Nach erfolgreicher Entschlüsselung die Ansicht aktualisieren
                        (this._veraportal as Schuelerverwaltung).refreshSchuelerverwaltung();
                    } catch {
                        return false;
                    }
                } catch {
                    // Bei falschem Entschlüsselungscode: Fehlermeldung anzeigen und localStorage zurücksetzen
                    let hinweisModal = new Modal(this._veraportal);
                    hinweisModal.show({
                        title: `Entschlüsselungscode falsch`,
                        body: `<p>Der von Ihnen eingegebene Entschlüsselungscode war leider nicht richtig.<br/>
                                            Versuchen Sie es gerne erneut oder erfragen Sie im Kollegium den korrekten Entschlüsselungscode.</p>
                                            <p>Sollten Sie den Entschlüsselungscode dauerhaft verloren haben,
                                            können Sie über unseren Support die Klarnamenfunktion vollständig zurücksetzen lassen.</p>`,
                        hasSubmit: false,
                        hasCancel: true,
                        hasBestaetigung: false,
                        cancelText: 'OK',
                        width: 400,
                    });
                    localStorage.removeItem('tresor-passphrase');
                    localStorage.removeItem('tresor-salt');
                    localStorage.removeItem('tresor-encrypted');
                    return false;
                }

                // Statistik zur Nutzungshäufigkeit der Importfunktion eingefügt
                const stat = Statistik.getInstance();
                stat.pushStat('klarnamen-entschluesselt', '1');

                return true;
            }
        });
    }

    /**
     * Initialisiert einen neuen Tresor, wenn die Funktion zum ersten Mal verwendet wird.
     * Generiert einen 12-stelligen Entschlüsselungscode, zeigt diesen dem Nutzer an
     * und speichert die initialen (leeren) Tresordaten.
     * @private
     */
    private async initialisieren() {
        if ([1, 2].includes(this._veraportal.sessionvariablen.user.accounttyp) === false) {
            let klarnamenNurKoordinatorHinweisModal = new Modal(this._veraportal);
            await klarnamenNurKoordinatorHinweisModal.show({
                title: `Erstmalige Aktivierung der Klarnamen nicht erlaubt`,
                body: `
                <p>
                    Nur der Koordinator Ihres Jahrgangs kann die Klarnamenfunktion <b>erstmalig aktivieren</b>.
                </p>
                <p>
                    Die Klarnamen sind nachträglich durch alle Lehrkräfte mit dem Entschlüsselungscode einsehbar, welcher vom Koordinator distribuiert wurde.
                </p>
                `,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 600,
                cancelText: 'OK'
            });
            return;
        }

        let klarnamenWirklichAktivierenModal = new Modal(this._veraportal);
        let klarnamenWirklichAktivierenModalPromise = klarnamenWirklichAktivierenModal.show({
            title: `Möchten Sie Klarnamenfunktion wirklich erstmalig aktivieren?`,
            body: `
                <p>
                    Wenn Sie fortfahren, erhalten Sie einen Entschlüsselungscode. Sie sollten diesen dann notieren und anschließend mit Ihrem Kollegium teilen.
                    Der Code ist nur Ihnen und Ihren Kollegen bekannt und stellt sicher, dass Unbefugte keine Klarnamen Ihrer Schüler einsehen können.
                </p>
                <p>
                    Um fortzufahren, geben Sie im Bestätigungsfeld <i>"OK"</i> ein.
                </p>
                `,
            hasSubmit: true,
            hasCancel: true,
            hasBestaetigung: true,
            width: 600,
            cancelText: 'Abbrechen',
            submitText: 'Klarnamenfunktion<br/>aktivieren'
        });

        if (await klarnamenWirklichAktivierenModalPromise === false)
            return;

        this._passphrase = this.makePassphrase(12, 'ACDEFGHJKLMNPQRSTUVWXYZ2345679');

        // Modal zur Anzeige des neuen Entschlüsselungscodes
        let tresorGenerierenModal = new Modal(this._veraportal);
        tresorGenerierenModal.show({
            id: `modal-tresor-generieren`,
            title: `Klarnamenspeicherung aktiviert`,
            body: `<p>Den folgenden Entschlüsselungscode sollten Sie <b>jetzt notieren</b> und
                    anschließend mit Ihrem <b>Kollegium teilen</b>.<br/>
                    Der Code ist nur Ihnen und Ihren Kollegen bekannt und stellt sicher, dass Unbefugte
                    keine Klarnamen Ihrer Schüler einsehen können.
                    <span class="entschluesselungscode" data-tooltip="${Tooltip.html`
                        <p><strong>Entschlüsselungscode kopieren</strong></p>
                        <p>Klicken Sie auf den Code, um ihn in die Zwischenablage zu kopieren.</p>
                    `}">${this._passphrase.substring(0, 4) + ' ' + this._passphrase.substring(4, 8) + ' ' + this._passphrase.substring(8, 12)}</span>
                    </p>`,
            initFunction: () => {
                $(`span.entschluesselungscode`).on('click', () => {
                    navigator.clipboard.writeText(this._passphrase);
                });
            },
            hasSubmit: false,
            hasCancel: true,
            hasBestaetigung: false,
            cancelText: 'OK',
            width: 600,
        });

        // Entschlüsselungscode und ein zufälliges Salt generieren und im localStorage speichern
        this._salt = CryptoJS.MD5(Math.random().toString()).toString();
        localStorage.setItem("tresor-passphrase", JSON.stringify(this._passphrase));
        localStorage.setItem("tresor-salt", JSON.stringify(this._salt));

        // Leeres Schueler-Array als Startpunkt initialisieren
        this._decrypted = {schuelers: []};

        // Das leere Array verschlüsseln und im localStorage sowie auf dem Server speichern
        let kdf = CryptoJS.PBKDF2(this._passphrase, this._salt, {keySize: 512 / 32, iterations: 1000});
        let komprimiert = await Utilities.compressStringWithGzip(JSON.stringify(this._decrypted));
        this._encrypted = CryptoJS.AES.encrypt(
            komprimiert, CryptoJS.enc.Base64.stringify(kdf)
        ).toString();
        localStorage.setItem("tresor-encrypted", JSON.stringify(this._encrypted));

        this.offen = true;
        document.dispatchEvent(this._eventOnTresorOffen);
        document.querySelector(`#tresor-schalter[data-tresor-offen]`).setAttribute('data-tresor-offen', '1');
        try {
            // Ansicht aktualisieren, um die Eingabefelder für Klarnamen freizuschalten
            (this._veraportal as Schuelerverwaltung).refreshSchuelerverwaltung();
        } catch {
            //
        }

        this.postTresor();
    }

    /**
     * Generiert eine zufällige Passphrase.
     * @param {number} length - Die Länge der Passphrase.
     * @param {string} characters - Der Zeichensatz, aus dem die Passphrase generiert wird.
     * @returns {string}
     * @private
     */
    private makePassphrase(length: number = 12, characters: string = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz123456789'): string {
        let result = '';
        let charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    /**
     * Liest die Klarnamen aus den Eingabefeldern im DOM, aktualisiert das `_decrypted`-Objekt,
     * verschlüsselt die Daten neu und speichert sie auf dem Server.
     * @returns {Promise<boolean>}
     */
    public async writeKlarnamenToBlob(): Promise<boolean> {
        if (document.querySelectorAll(`input.tresor.schuelername[data-changed='1']`)?.length < 1) return false;

        try {
            let klarnamenDOM: NodeListOf<HTMLInputElement> = document.querySelectorAll(`input.tresor.schuelername`);
            for (let k = 0; k < klarnamenDOM.length; k++) {
                let schuelernummer: number = parseInt(klarnamenDOM[k].closest(`div.schueler`)?.getAttribute('data-nr')) ?? null;
                if (schuelernummer < 1) continue;
                let klarname = klarnamenDOM[k].value.trim() ?? null;
                klarname = (klarname === '') ? null : klarname;

                let thisSchueler = this._decrypted.schuelers.filter((s) => {
                    return s.nr === schuelernummer
                })[0] ?? null;
                if (thisSchueler === null) {
                    this._decrypted.schuelers.push({
                        nr: schuelernummer,
                        klarname: klarname
                    });
                } else {
                    thisSchueler.klarname = klarname;
                }
            }

            // Aktualisiertes Schueler-Array verschlüsseln, im localStorage ablegen und zum Server senden
            let kdf = CryptoJS.PBKDF2(this._passphrase, this._salt, {keySize: 512 / 32, iterations: 1000});
            let komprimiert = await Utilities.compressStringWithGzip(JSON.stringify(this._decrypted));
            this._encrypted = CryptoJS.AES.encrypt(
                komprimiert, CryptoJS.enc.Base64.stringify(kdf)
            ).toString();
            localStorage.setItem("tresor-encrypted", JSON.stringify(this._encrypted));
            document.querySelectorAll(`input.tresor.schuelername[data-changed='1']`)
                .forEach(i => i.setAttribute('data-changed', '0'));

            return await this.postTresor();
        } catch (e) {
            Utilities.errorToSentry(e);

            return false;
        }
    }

    /**
     * Importiert ein Array von Klarnamen, verschlüsselt und speichert sie.
     * @param {{nr: number, klarname: string}[]} klarnamenArray - Das Array der zu importierenden Klarnamen.
     * @returns {Promise<boolean>}
     */
    public async datenimportKlarnamen(klarnamenArray: { nr: number; klarname: string; }[]): Promise<boolean> {
        if (klarnamenArray.length < 1)
            return false;

        try {
            this._decrypted.schuelers = klarnamenArray;

            // Schueler-Array verschlüsseln (AES mit KeyDerivationFunction) und auch im localStorage hinterlegen
            let kdf = CryptoJS.PBKDF2(this._passphrase, this._salt, {keySize: 512 / 32, iterations: 1000});
            let komprimiert = await Utilities.compressStringWithGzip(JSON.stringify(this._decrypted));
            this._encrypted = CryptoJS.AES.encrypt(
                komprimiert, CryptoJS.enc.Base64.stringify(kdf)
            ).toString();
            localStorage.setItem("tresor-encrypted", JSON.stringify(this._encrypted));

            return await this.postTresor();
        } catch (e) {
            Utilities.errorToSentry(e);

            return false;
        }
    }

    /**
     * Importiert ein Array von Klarnamen (spezifisch für Bremen), verschlüsselt und speichert sie.
     * @param {{nr: number, klarname: string}[]} klarnamenArray - Das Array der zu importierenden Klarnamen.
     * @returns {Promise<boolean>}
     */
    public async importKlarnamenBremen(klarnamenArray: { nr: number; klarname: string; }[]): Promise<boolean> {
        if (klarnamenArray.length < 1) return false;

        try {
            this._decrypted.schuelers = klarnamenArray;

            // Schueler-Array verschlüsseln (AES mit KeyDerivationFunction) und auch im localStorage hinterlegen
            let kdf = CryptoJS.PBKDF2(this._passphrase, this._salt, {keySize: 512 / 32, iterations: 1000});
            let komprimiert = await Utilities.compressStringWithGzip(JSON.stringify(this._decrypted));
            this._encrypted = CryptoJS.AES.encrypt(
                komprimiert, CryptoJS.enc.Base64.stringify(kdf)
            ).toString();
            localStorage.setItem("tresor-encrypted", JSON.stringify(this._encrypted));

            return await this.postTresor();
        } catch {
            return false;
        }
    }

    /**
     * Initialisiert die Eingabefelder für Klarnamen in der Schuelerverwaltung.
     * Setzt Event-Listener für Änderungen, Eingabefilter und das Einfügen von Daten aus der Zwischenablage.
     * Befüllt die Felder anschließend mit den entschlüsselten Namen.
     */
    public klarnamenFelderInitialisieren() {
        // EventListener zum Markieren von geänderten Feldern
        $(`input.tresor.schuelername`).off('change.klarnamen');
        $(`input.tresor.schuelername`).on('change.klarnamen', (evt) => {
            (evt.target as HTMLInputElement)?.setAttribute('data-changed', '1');
        });

        // EventListener zur Validierung der Eingabe (nur erlaubte Zeichen)
        $(`input.tresor.schuelername`).off('keyup.eingabeklarnamen');
        $(`input.tresor.schuelername`).on('keyup.eingabeklarnamen', (evt) => {
            Utilities.forceKeys($(evt.target)[0] as HTMLInputElement, 'A-Za-z0-9äÄüÜöÖß\\-\\_\\ \\.', false, true);
        });

        // Event-Handler für das Einfügen von Klarnamen aus der Zwischenablage (z.B. aus Excel)
        document.querySelectorAll(`input.tresor.schuelername`).forEach(item => {
            item.removeAttribute('disabled');

            item.addEventListener('paste', (evt: ClipboardEvent) => {
                let clipboardTable = this._veraportal.excelClipboardToArray(evt);

                // Wenn mehrere Zeilen, aber nur eine Spalte eingefügt werden, diese in die folgenden Felder verteilen.
                if (clipboardTable.length > 1 && clipboardTable[0].length === 1) {
                    let schuelerNrAnfang: number = parseInt((evt.target as HTMLInputElement).closest(`div.schueler`)?.getAttribute('data-nr') ?? '0');
                    this.schuelernamenEinfuegen(schuelerNrAnfang, clipboardTable);
                } else
                    (evt.target as HTMLInputElement)?.setAttribute('data-changed', '1');
            });
        });

        // Die Eingabefelder mit den entschlüsselten Klarnamen befüllen
        if (this._decrypted.schuelers === undefined) return;
        try {
            let klarnamenDOM: NodeListOf<HTMLInputElement> = document.querySelectorAll(`input.tresor.schuelername`);
            for (let k = 0; k < klarnamenDOM.length; k++) {
                let schuelernummer: number = parseInt(klarnamenDOM[k].closest(`div.schueler`)?.getAttribute('data-nr')) ?? null;
                if (schuelernummer < 1) continue;

                let thisSchueler = this._decrypted.schuelers.filter((s) => {
                    return s.nr === schuelernummer
                })[0] ?? null;

                if ((thisSchueler?.klarname ?? null) !== null) {
                    klarnamenDOM[k].value = thisSchueler.klarname?.trim() ?? '';
                    klarnamenDOM[k].setAttribute('data-changed', '0');
                }
            }
        } catch (e) {
            Utilities.errorToSentry(e);
        }
    }

    /**
     * Befüllt Platzhalter-Elemente im gesamten Portal mit den entschlüsselten Klarnamen.
     * Die Platzhalter sind `<span>`-Elemente mit dem Attribut `data-tresor-anzeige='schueler'` und `data-nr='x'`.
     */
    public fillTresorLuecken() {
        let luecken: NodeListOf<HTMLSpanElement> = document.querySelectorAll(`span[data-tresor-anzeige]`);
        for (let l = 0; l < luecken.length; l++) {
            let text = '';
            let keinKlarname = false;

            // Klarname basierend auf der Schülernummer aus dem `_decrypted`-Objekt extrahieren
            if (luecken[l].matches(`[data-tresor-anzeige='schueler'][data-nr]`) === true) {
                try {
                    const klarname = this._decrypted.schuelers.find((s) => {
                        return s.nr === parseInt(luecken[l].getAttribute('data-nr'));
                    })?.klarname?.trim() ?? '';
                    keinKlarname = klarname === '';
                    text = keinKlarname ? '-' : klarname;
                } catch {
                    continue;
                }
            } else {
                continue;
            }

            // Text in das Platzhalter-Element einfügen, optional mit Prä- und Suffix
            luecken[l].textContent = (luecken[l].getAttribute('data-left') ?? '') + text + (luecken[l].getAttribute('data-right') ?? '');
            luecken[l].classList.toggle('ohne-klarname', keinKlarname);
            if (keinKlarname) {
                luecken[l].dataset.tooltip = Tooltip.content`<p>Für diese Schülerin bzw. diesen Schüler wurde kein Klarname hinterlegt.</p>`;
            } else {
                delete luecken[l].dataset.tooltip;
            }
        }
    }

    /**
     * Fügt eine Liste von Schülernamen aus der Zwischenablage in die entsprechenden Eingabefelder ein.
     * @param {number} schuelerNrAnfang - Die Schülernummer, bei der das Einfügen beginnt.
     * @param {string[][]} clipboardTable - Die aus der Zwischenablage geparsten Daten.
     * @returns {boolean}
     * @private
     */
    private async schuelernamenEinfuegen(schuelerNrAnfang: number, clipboardTable: string[][]): Promise<boolean> {
        if (schuelerNrAnfang < 1)
            return false;
        if (clipboardTable.length < 1)
            return false;

        try {
            // Wenn die erste Zeile wie eine Kopfzeile aussieht, wird sie entfernt.
            if (
                clipboardTable[0].map(c => c.toLowerCase()).includes('geschlecht') ||
                clipboardTable[0].map(c => c.toLowerCase()).includes('klarname') ||
                clipboardTable[0].map(c => c.toLowerCase()).includes('bezeichnung') ||
                clipboardTable[0].map(c => c.toLowerCase()).includes('name') ||
                clipboardTable[0].map(c => c.toLowerCase()).includes('vorname') ||
                clipboardTable[0].map(c => c.toLowerCase()).includes('nachname') ||
                clipboardTable[0].map(c => c.toLowerCase()).includes('pseudonym')
            ) {
                clipboardTable.shift();
            }

            let hinweisModal = new Modal(this._veraportal);
            let hinweisModalAuswahl = await hinweisModal.show({
                title: `Schülernamen einfügen`,
                body: `
                    <p>
                        Wollen Sie wirklich ${clipboardTable.length} Schülernamen in die darunterliegenden Felder einfügen?
                    </p>
                `,
                hasSubmit: true,
                hasCancel: true,
                hasBestaetigung: false,
                width: 400,
                submitText: 'Einfügen',
                cancelText: 'Abbrechen'
            });
            if (hinweisModalAuswahl === false)
                return;

            // Klarnamen in die sichtbaren und aktiven Textfelder eintragen
            let alleAktivenSchuelerKlarnamen: NodeListOf<HTMLInputElement> = document
                .querySelectorAll(`div.schuelerverwaltung > div.schuelerliste > div.container div.schueler[data-deaktiviert='0'] input.schuelername.tresor`);

            let pasteCounter = 0;
            for (let k = 0; k < alleAktivenSchuelerKlarnamen.length; k++) {
                // Erst bei der Schülernummer des Zielfeldes mit dem Einfügen beginnen
                if (parseInt(alleAktivenSchuelerKlarnamen[k].closest(`div.schueler`)?.getAttribute('data-nr') ?? '0') < schuelerNrAnfang)
                    continue;

                // Namen einfügen und das Feld als geändert markieren
                if (pasteCounter < clipboardTable.length) {
                    (alleAktivenSchuelerKlarnamen[k] as HTMLInputElement).value = clipboardTable[pasteCounter][0];
                    (alleAktivenSchuelerKlarnamen[k] as HTMLInputElement)?.setAttribute('data-changed', '1');
                    pasteCounter++;
                } else {
                    break;
                }
            }
            return true;
        } catch (e) {
            Utilities.errorToSentry(e);
            return false;
        }
    }

    /**
     * Füllt die Formularfelder eines PDF-Dokuments mit den entschlüsselten Klarnamen und löst den Download aus.
     * @param {string} pdfName - Der Dateiname für das heruntergeladene PDF.
     * @param {string} pdfUrl - Die URL der PDF-Vorlage.
     * @returns {Promise<boolean>}
     */
    public async putKlarnamenIntoPdf(pdfName: string, pdfUrl: string): Promise<boolean> {
        try {
            const {PDFDocument} = PDFLib;
            const formPdfBytes: ArrayBuffer = await fetch(pdfUrl).then(res => res.arrayBuffer());
            const pdfDoc: PDFDocument = await PDFDocument.load(formPdfBytes);
            const form: PDFForm = pdfDoc.getForm();

            if (this.offen === true) {
                for (let i = 1; i < 999; i++) {
                    try {
                        const nameField = form.getTextField(`schueler${i}`);
                        let thisSchueler = this._decrypted.schuelers.filter((s) => {
                            return s.nr === i
                        })[0] ?? null;
                        if (thisSchueler !== null)
                            nameField.setText(thisSchueler.klarname ?? '');
                    } catch (e) {
                        // Fehler wird ignoriert, wenn ein Feld (z.B. `schueler28`) nicht im PDF existiert.
                    }

                }
                form.flatten();
            }

            const pdfBytes = new Uint8Array(await pdfDoc.save());
            const blob = new Blob([pdfBytes], {type: "application/pdf"});
            // @ts-ignore
            saveAs(blob, pdfName);
            return true;
        } catch (e) {
            Utilities.errorToSentry(e);

            return false;
        }
    }

}
